import { useCallback, useEffect, useState } from 'react'
import { HttpStatusCode } from 'axios'
import type { Entity } from '@/modules/shared/types/entity.type'
import { useEntitySearch } from '@/modules/shared/hooks/use-entity-search.hook'
import { useOrderFormCalculation } from '../../../hooks/use-order-form-calculation.hook'
import { useOrderFormComponentStore, useOrderFormComponentStoreActions } from '../../../stores/order-form-component.store'
import { useOrderFormStore } from '../../../stores/order-form.store'
import type { IOrderFormJson } from '../../../types/order-form-json.type'
import { useAxiosHttpClient } from '@/modules/shared/infra/axios-http-client.infra'
import { orderFormDataComponentService } from '../../../services/order-form-data-component.service'
import { useToast } from '@/modules/shared/hooks/use-toast'
import type { PayloadSearch } from '@/modules/shared/types/payload-search.type'

export type EntityType = 'client' | 'transport' | 'seller'

export function useEntitySelection() {
  const [newJsonOrderForm, setNewJsonOrderForm] = useState<IOrderFormJson>()
  const { toast } = useToast()

  const entitySearch = useEntitySearch()
  const { calculateOrderForm } = useOrderFormCalculation()
  const { isOpenModalChangeCustomerData } = useOrderFormComponentStore()

  const {
    setIsOpenOrderFormModalEntity,
    setEntityType,
    setIsOpenModalChangeCustomerData,
    setCustomers,
    setSellers,
    setTransports,
  } = useOrderFormComponentStoreActions

  const {
    jsonOrderForm,
    paramsOrderForm,
    actions: { setJsonOrderForm, setEntityTypeSelected },
  } = useOrderFormStore()

  // Requests HTTP para cada tipo de entidade
  const { axiosRequest: requestClient } = useAxiosHttpClient({
    sendRequest: orderFormDataComponentService,
  })

  const { axiosRequest: requestSellers } = useAxiosHttpClient({
    sendRequest: orderFormDataComponentService,
  })

  const { axiosRequest: requestTransport } = useAxiosHttpClient({
    sendRequest: orderFormDataComponentService,
  })

  const operationTypeOfOrder = {
    1: paramsOrderForm?.listaOperacaoJuridica,
    2: paramsOrderForm?.listaOperacaoFisica,
  }

  // Função unificada para abrir modal
  const openModal = useCallback((type: EntityType) => {
    setEntityType(type)
    setIsOpenOrderFormModalEntity(true)
    entitySearch.resetSearch()
  }, [setEntityType, setIsOpenOrderFormModalEntity, entitySearch])

  // Função unificada para fechar modal
  const closeModal = useCallback(() => {
    setIsOpenOrderFormModalEntity(false)
    entitySearch.resetSearch()
  }, [setIsOpenOrderFormModalEntity, entitySearch])

  // Função unificada para buscar entidades
  const searchEntities = useCallback((type: EntityType, filter: string, inputValue: string) => {
    const entityTypeMap = {
      client: '1',
      transport: '3',
      seller: '4'
    }

    entitySearch.searchEntities(
      {
        entityType: entityTypeMap[type],
        filter,
        inputValue
      },
      jsonOrderForm?.dataInclusao
    )
  }, [entitySearch, jsonOrderForm?.dataInclusao])

  // Função específica para buscar transporte (mantém lógica original)
  const handleSearchTransport = useCallback(({ payload }: PayloadSearch) => {
    if (payload.inputValue.length < 1) {
      return toast.info({
        title: 'Por favor, insira mais informações para realizar a pesquisa',
      })
    }

    const newPayload = {
      entityType: '3',
      filter: payload.filter,
      inputValue:
        payload.filter === 'cnpjCpf'
          ? payload.inputValue.replace(/\D/g, '')
          : payload.inputValue,
    }

    requestTransport.mutate({
      payload: newPayload,
      showInfo: true,
    })
  }, [toast, requestTransport])

  // Seleção de cliente (mantém lógica original)
  const selectClient = useCallback(async (entity: Entity) => {
    const entityTypeSelected = entity?.flagPessoa === '2' ? '1' : '2'
    const isConfirmationRequired =
      jsonOrderForm.fkCliente &&
      jsonOrderForm.fkCliente !== String(entity.idEntidade)

    const newJsonOrderForm: IOrderFormJson = {
      ...jsonOrderForm,
      fkCliente: String(entity.idEntidade),
      fkOperacao: String(
        operationTypeOfOrder[entityTypeSelected][0]?.idOperacao ?? '1',
      ),
    }

    closeModal()
    setJsonOrderForm(newJsonOrderForm)
    setEntityTypeSelected(entityTypeSelected)
    setNewJsonOrderForm(newJsonOrderForm) // Armazena para os modais de confirmação

    if (isConfirmationRequired) {
      setIsOpenModalChangeCustomerData(true)
    } else {
      calculateOrderForm({
        ...newJsonOrderForm,
        parametros: {
          ...newJsonOrderForm.parametros,
          atualizaDadosCliente: true,
        },
      })
    }
  }, [jsonOrderForm, operationTypeOfOrder, closeModal, setJsonOrderForm, setEntityTypeSelected, setIsOpenModalChangeCustomerData, calculateOrderForm])

  // Seleção de transporte (mantém lógica original)
  const selectTransport = useCallback((entity: Entity) => {
    const newJsonOrderForm = {
      ...jsonOrderForm,
      redespacho: entity,
      fkRedespacho: String(entity.idEntidade),
    }

    closeModal()
    setJsonOrderForm(newJsonOrderForm)
    calculateOrderForm(newJsonOrderForm)
  }, [jsonOrderForm, closeModal, setJsonOrderForm, calculateOrderForm])

  // Seleção de vendedor (mantém lógica original)
  const selectSeller = useCallback(async (entity: Entity) => {
    const newJsonOrderForm: IOrderFormJson = {
      ...jsonOrderForm,
      fkVendedor: entity?.idEntidade.toString(),
    }

    closeModal()
    setJsonOrderForm(newJsonOrderForm)
    await calculateOrderForm(newJsonOrderForm)
  }, [jsonOrderForm, closeModal, setJsonOrderForm, calculateOrderForm])

  // Handlers para modal de confirmação de mudança de cliente
  const handleModalConfirmChangeCustomer = useCallback(() => {
    setIsOpenModalChangeCustomerData(false)
    if (newJsonOrderForm?.parametros) {
      newJsonOrderForm.parametros.atualizaDadosCliente = true
    }
    calculateOrderForm(newJsonOrderForm)
  }, [newJsonOrderForm, setIsOpenModalChangeCustomerData, calculateOrderForm])

  const handleModalCancelChangeCustomer = useCallback(() => {
    setIsOpenModalChangeCustomerData(false)
    calculateOrderForm(newJsonOrderForm)
  }, [newJsonOrderForm, setIsOpenModalChangeCustomerData, calculateOrderForm])

  // Effects para processar responses HTTP
  useEffect(() => {
    if (requestClient.isSuccess) {
      if (requestClient.data.status === HttpStatusCode.Ok) {
        setCustomers(JSON.parse(requestClient.data.body))
        return
      }
    }
  }, [requestClient.isLoading, requestClient.isSuccess, requestClient.data, setCustomers])

  useEffect(() => {
    if (requestSellers.isSuccess) {
      if (requestSellers.data.status === HttpStatusCode.Ok) {
        setSellers(JSON.parse(requestSellers.data.body))
        return
      }
      setIsOpenOrderFormModalEntity(false)
    }
  }, [requestSellers.isLoading, requestSellers.isSuccess, requestSellers.data, setSellers, setIsOpenOrderFormModalEntity])

  useEffect(() => {
    if (requestTransport.isSuccess) {
      if (requestTransport.data.status === HttpStatusCode.Ok) {
        setTransports(JSON.parse(requestTransport.data.body))
        return
      }
      setIsOpenOrderFormModalEntity(false)
    }
  }, [requestTransport.isLoading, requestTransport.isSuccess, requestTransport.data, setTransports, setIsOpenOrderFormModalEntity])

  return {
    // Estados do entitySearch
    ...entitySearch,

    // Estados específicos
    isOpenModalChangeCustomerData,

    // Funções principais
    openModal,
    closeModal,
    searchEntities,
    handleSearchTransport, // Função específica para transporte

    // Funções de seleção
    selectClient,
    selectTransport,
    selectSeller,

    // Handlers para modal de confirmação
    handleModalConfirmChangeCustomer,
    handleModalCancelChangeCustomer,
  }
}